import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authenticatedGet, authenticatedPost, authenticatedPut, authenticatedDelete } from '@/utils/api'
import { useRootStore } from './root'
import { usePerimeterStore } from './perimeter'
import { useSimulationStore } from './simulation'
import { useSolvencyStore } from './solvency'
// DÉSACTIVÉ - Modules ESG et Risk
// import { useEsgStore } from './esg'
// import { useRiskStore } from './risk'
import { useTransparisationStore } from './transparisation'

export interface Archive {
  perimeterID: number
  name: string
  category: string
  simulationDate: string
  displayName: string
  portfolioNames: string
  lookThroughLevel?: number
  scr: boolean
  esg: boolean
  riskLiquidity: boolean
  simulation: boolean
}

export const useArchivesStore = defineStore('archives', () => {
  const rootStore = useRootStore()
  const perimeterStore = usePerimeterStore()
  const simulationStore = useSimulationStore()

  // State
  const archives = ref<Archive[]>([])
  const categories = ref<string[]>([])
  const loading = ref(false)
  const creating = ref(false)
  const created = ref(false)
  const updating = ref(false)
  const deleting = ref(false)
  const createDialogOpened = ref(false)
  const selectedArchiveScr = ref<string | undefined>(undefined)
  const selectedArchiveEsg = ref<string | undefined>(undefined)
  const selectedArchiveRisk = ref<string | undefined>(undefined)
  const selectedArchiveTranspa = ref<string | undefined>(undefined)
  
  // Cache pour éviter les appels API répétés
  const CACHE_TTL = 60 * 1000 // 1 minute
  const lastFetch = ref<{ timestamp: number, clientCode: string } | null>(null)

  // Computed - Archives filtrées par module
  const archivesScr = computed(() => archives.value.filter(e => e.scr))
  const archivesEsg = computed(() => archives.value.filter(e => e.esg))
  const archivesRisk = computed(() => archives.value.filter(e => e.riskLiquidity))
  const archivesTranspa = computed(() => archives.value.filter(e => !e.scr && !e.esg && !e.riskLiquidity))

  // Computed - Archives les plus récentes
  const mostRecentArchive = computed(() => {
    if (archives.value.length === 0) return null
    const sorted = [...archives.value].sort((a, b) => {
      const dateA = new Date(a.simulationDate)
      const dateB = new Date(b.simulationDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted[0]
  })

  const mostRecentArchiveScr = computed(() => {
    if (archivesScr.value.length === 0) return null
    const sorted = [...archivesScr.value].sort((a, b) => {
      const dateA = new Date(a.simulationDate)
      const dateB = new Date(b.simulationDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted[0]
  })

  const mostRecentArchiveEsg = computed(() => {
    if (archivesEsg.value.length === 0) return null
    const sorted = [...archivesEsg.value].sort((a, b) => {
      const dateA = new Date(a.simulationDate)
      const dateB = new Date(b.simulationDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted[0]
  })

  const mostRecentArchiveRisk = computed(() => {
    if (archivesRisk.value.length === 0) return null
    const sorted = [...archivesRisk.value].sort((a, b) => {
      const dateA = new Date(a.simulationDate)
      const dateB = new Date(b.simulationDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted[0]
  })

  // Computed - Catégories par défaut
  const defaultCategory = computed(() => {
    if (archives.value.length === 0) return ''
    const hasOfficiel = archives.value.findIndex(e => e.category === 'Officiel') !== -1
    return hasOfficiel ? 'Officiel' : archives.value[0]?.category || ''
  })

  const defaultCategoryScr = computed(() => {
    if (archivesScr.value.length === 0) return ''
    const hasOfficiel = archivesScr.value.findIndex(e => e.category === 'Officiel') !== -1
    return hasOfficiel ? 'Officiel' : archivesScr.value[0]?.category || ''
  })

  const defaultCategoryEsg = computed(() => {
    if (archivesEsg.value.length === 0) return ''
    const hasOfficiel = archivesEsg.value.findIndex(e => e.category === 'Officiel') !== -1
    return hasOfficiel ? 'Officiel' : archivesEsg.value[0]?.category || ''
  })

  const defaultCategoryRisk = computed(() => {
    if (archivesRisk.value.length === 0) return ''
    const hasOfficiel = archivesRisk.value.findIndex(e => e.category === 'Officiel') !== -1
    return hasOfficiel ? 'Officiel' : archivesRisk.value[0]?.category || ''
  })

  // Computed - Portefeuilles par défaut pour historique
  const defaultPortfolioHistory = computed(() => {
    if (archives.value.length === 0 || !defaultCategory.value) return ''
    const filtered = archives.value.filter(e => e.category === defaultCategory.value)
    if (filtered.length === 0) return ''
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.simulationDate)
      const dateB = new Date(b.simulationDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted[0]?.portfolioNames || ''
  })

  const defaultPortfolioHistoryScr = computed(() => {
    if (archivesScr.value.length === 0 || !defaultCategoryScr.value) return ''
    const filtered = archivesScr.value.filter(e => e.category === defaultCategoryScr.value)
    if (filtered.length === 0) return ''
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.simulationDate)
      const dateB = new Date(b.simulationDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted[0]?.portfolioNames || ''
  })

  const defaultPortfolioHistoryEsg = computed(() => {
    if (archivesEsg.value.length === 0 || !defaultCategoryEsg.value) return ''
    const filtered = archivesEsg.value.filter(e => e.category === defaultCategoryEsg.value)
    if (filtered.length === 0) return ''
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.simulationDate)
      const dateB = new Date(b.simulationDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted[0]?.portfolioNames || ''
  })

  const defaultPortfolioHistoryRisk = computed(() => {
    if (archivesRisk.value.length === 0 || !defaultCategoryRisk.value) return ''
    const filtered = archivesRisk.value.filter(e => e.category === defaultCategoryRisk.value)
    if (filtered.length === 0) return ''
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.simulationDate)
      const dateB = new Date(b.simulationDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted[0]?.portfolioNames || ''
  })

  // Computed - Indicateurs d'absence d'archives
  const noArchive = computed(() => archives.value.length === 0)
  const noArchiveScr = computed(() => archivesScr.value.length === 0)
  const noArchiveEsg = computed(() => archivesEsg.value.length === 0)
  const noArchiveRisk = computed(() => archivesRisk.value.length === 0)

  // Computed - Utilisateurs uniques
  const users = computed(() => {
    const uniqueUsers: string[] = []
    archives.value.forEach(archive => {
      if (!uniqueUsers.includes(archive.displayName)) {
        uniqueUsers.push(archive.displayName)
      }
    })
    return uniqueUsers
  })

  // Actions
  const setCreateDialogOpened = (value: boolean) => {
    createDialogOpened.value = value
  }

  const setSelectedArchiveScr = (perimeterID: number) => {
    const archive = archives.value.find(e => e.perimeterID === perimeterID)
    selectedArchiveScr.value = archive?.name
  }

  const setSelectedArchiveEsg = (perimeterID: number) => {
    const archive = archives.value.find(e => e.perimeterID === perimeterID)
    selectedArchiveEsg.value = archive?.name
  }

  const setSelectedArchiveRisk = (perimeterID: number) => {
    const archive = archives.value.find(e => e.perimeterID === perimeterID)
    selectedArchiveRisk.value = archive?.name
  }

  const setSelectedArchiveTranspa = (perimeterID: number) => {
    const archive = archives.value.find(e => e.perimeterID === perimeterID)
    selectedArchiveTranspa.value = archive?.name
  }

  const resetSelectedArchives = () => {
    selectedArchiveScr.value = undefined
    selectedArchiveEsg.value = undefined
    selectedArchiveRisk.value = undefined
    selectedArchiveTranspa.value = undefined
  }

  /**
   * Charge toutes les archives pour le client actuel
   */
  const getArchives = async (forceRefresh = false) => {
    const clientCode = perimeterStore.client?.code
    if (!clientCode) {
      archives.value = []
      return
    }

    // Vérifier le cache (sauf si forceRefresh demandé)
    if (!forceRefresh && 
        lastFetch.value && 
        lastFetch.value.clientCode === clientCode &&
        Date.now() - lastFetch.value.timestamp < CACHE_TTL) {
      return
    }

    loading.value = true

    try {
      const baseUrl = rootStore.baseUrl
      const url = `${baseUrl}/api/archive/all`
      
      const response = await authenticatedGet(url, { 
        params: { rolename: clientCode } 
      })

      // Trier par date décroissante
      const sorted = (response.data || []).sort((a: Archive, b: Archive) => {
        const dateA = new Date(a.simulationDate)
        const dateB = new Date(b.simulationDate)
        return dateB.getTime() - dateA.getTime()
      })

      archives.value = sorted
      loading.value = false

      // Mettre à jour le cache
      lastFetch.value = { timestamp: Date.now(), clientCode }

      // Charger les catégories
      await getCategories()
    } catch (err) {
      console.error('Error loading archives:', err)
      loading.value = false
      throw err
    }
  }

  /**
   * Charge les catégories d'archives
   */
  const getCategories = async () => {
    const clientCode = perimeterStore.client?.code
    if (!clientCode) {
      return
    }

    try {
      const baseUrl = rootStore.baseUrl
      const response = await authenticatedGet(`${baseUrl}/api/archive/categories`, {
        params: { rolename: clientCode }
      })

      const cats = response.data || []
      
      // Ajouter Officiel et Test si pas déjà présents
      if (!cats.includes('Officiel')) {
        cats.push('Officiel')
      }
      if (!cats.includes('Test')) {
        cats.push('Test')
      }

      categories.value = cats
    } catch (err) {
      console.error('Error loading categories:', err)
      throw err
    }
  }

  /**
   * Vérifie si un périmètre a une archive
   * Utilise les données déjà chargées en mémoire pour éviter un appel API superflu
   */
  const currentResultHasArchive = (perimeterID: number): boolean => {
    return archives.value.findIndex((e: Archive) => e.perimeterID === perimeterID) !== -1
  }

  /**
   * Crée une nouvelle archive
   */
  const createArchive = async (data: {
    name: string
    category: string
    scr?: boolean
    esg?: boolean
    risk?: boolean
  }) => {
    creating.value = true
    created.value = false

    try {
      let perimeterId: string | undefined = undefined
      let simulation = false

      const solvencyStore = useSolvencyStore()
      const transparisationStore = useTransparisationStore()

      // Déterminer le périmètre selon le module
      if (data.scr) {
        perimeterId = solvencyStore.scrMarketResult?.perimeter?.perimeterID
        simulation = solvencyStore.onSimulation
      } else if (data.esg || data.risk) {
        // Pour ESG et Risk, utiliser le même perimeterId que SCR pour le moment
        // TODO: Implémenter la récupération depuis les stores respectifs
        perimeterId = transparisationStore.perimeterId
        simulation = false
      } else {
        // Transparisation
        if (simulationStore.simulationMode) {
          perimeterId = simulationStore.perimeterId
          simulation = true
        } else {
          perimeterId = transparisationStore.perimeterId
          simulation = false
        }
      }

      if (!perimeterId) {
        throw new Error('No perimeter ID available for archive creation')
      }

      const baseUrl = rootStore.baseUrl
      await authenticatedPost(`${baseUrl}/api/archive`, {
        perimeterID: parseInt(perimeterId),
        name: data.name,
        category: data.category,
        scr: data.scr || false,
        esg: data.esg || false,
        riskLiquidity: data.risk || false,
        simulation: simulation,
      })

      // Recharger les archives (forcer le refresh pour avoir les nouvelles données)
      await getArchives(true)
      
      creating.value = false
      created.value = true
      createDialogOpened.value = false

      // Mettre à jour le flag d'existence d'archives dans le store solvency
      if (data.scr) {
        solvencyStore.setArchiveScrExists(true)
      }
      // TODO: ESG et Risk quand les méthodes seront disponibles
    } catch (err) {
      console.error('Error creating archive:', err)
      creating.value = false
      createDialogOpened.value = false
      throw err
    }
  }

  /**
   * Met à jour une archive existante
   */
  const updateArchive = async (data: {
    perimeterId: number
    name: string
    category: string
    simulation: boolean
  }) => {
    updating.value = true

    try {
      const baseUrl = rootStore.baseUrl
      await authenticatedPut(`${baseUrl}/api/archive`, {
        perimeterID: data.perimeterId,
        name: data.name,
        category: data.category,
        simulation: data.simulation,
      })

      // Recharger les archives (forcer le refresh pour avoir les nouvelles données)
      await getArchives(true)
      
      updating.value = false
    } catch (err) {
      console.error('Error updating archive:', err)
      updating.value = false
      throw err
    }
  }

  /**
   * Supprime une archive
   */
  const deleteArchive = async (perimeterId: number) => {
    deleting.value = true

    try {
      const baseUrl = rootStore.baseUrl
      await authenticatedDelete(`${baseUrl}/api/archive?perimeterID=${perimeterId}`)

      // Recharger les archives (forcer le refresh pour avoir les nouvelles données)
      await getArchives(true)
      
      deleting.value = false
    } catch (err) {
      console.error('Error deleting archive:', err)
      deleting.value = false
      throw err
    }
  }

  /**
   * Supprime plusieurs archives en lot
   */
  const deleteArchives = async (perimeterIds: number[]) => {
    if (perimeterIds.length === 0) return
    
    deleting.value = true

    try {
      const baseUrl = rootStore.baseUrl
      await authenticatedDelete(`${baseUrl}/api/archive/bulk`, {
        data: perimeterIds
      })

      // Recharger les archives (forcer le refresh pour avoir les nouvelles données)
      await getArchives(true)
      
      deleting.value = false
    } catch (err) {
      console.error('Error deleting archives:', err)
      deleting.value = false
      throw err
    }
  }

  const reset = () => {
    archives.value = []
    categories.value = []
    loading.value = false
    creating.value = false
    created.value = false
    updating.value = false
    deleting.value = false
    createDialogOpened.value = false
    lastFetch.value = null // Réinitialiser le cache
    resetSelectedArchives()
  }

  return {
    // State
    archives,
    categories,
    loading,
    creating,
    created,
    updating,
    deleting,
    createDialogOpened,
    selectedArchiveScr,
    selectedArchiveEsg,
    selectedArchiveRisk,
    selectedArchiveTranspa,

    // Computed
    archivesScr,
    archivesEsg,
    archivesRisk,
    archivesTranspa,
    mostRecentArchive,
    mostRecentArchiveScr,
    mostRecentArchiveEsg,
    mostRecentArchiveRisk,
    defaultCategory,
    defaultCategoryScr,
    defaultCategoryEsg,
    defaultCategoryRisk,
    defaultPortfolioHistory,
    defaultPortfolioHistoryScr,
    defaultPortfolioHistoryEsg,
    defaultPortfolioHistoryRisk,
    noArchive,
    noArchiveScr,
    noArchiveEsg,
    noArchiveRisk,
    users,

    // Actions
    setCreateDialogOpened,
    setSelectedArchiveScr,
    setSelectedArchiveEsg,
    setSelectedArchiveRisk,
    setSelectedArchiveTranspa,
    resetSelectedArchives,
    getArchives,
    getCategories,
    currentResultHasArchive,
    createArchive,
    updateArchive,
    deleteArchive,
    deleteArchives,
    reset,
  }
}, {
  persist: {
    key: 'arago-archives',
    storage: sessionStorage,
    pick: ['selectedArchiveScr', 'selectedArchiveEsg', 'selectedArchiveRisk', 'selectedArchiveTranspa'],
  },
})
